import React, {useMemo} from "react";
import {Box, Button, Chip, Stack, Typography} from "@mui/material";
import moment from "moment-timezone";
import {orange} from "@mui/material/colors";
import {useTranslation} from "react-i18next";
import {useTimezone} from "../../hooks/useTimezone";
import {FULL_DAY_DATE_FORMAT, MONTH_YEAR_FORMAT} from "../../config/DateTimeConstants";
import {SHIFT_ADMIN_VIEWS} from "../../constants/ShiftAdminConstants";
import {calculateTotalForRowField} from "../../helpers/ShiftHelper";

const TODAY_CHIP_LABEL = "Today";
const THIS_MONTH_CHIP_LABEL = "This month";

const styles = {
    header: {
        padding: 1.5,
        border: "0.06rem solid",
        borderColor: "divider",
        borderRadius: 1,
        mb: 1.5,
    },
    headerContentStack: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 1,
    },
    dateContentStack: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
    },
    summaryContentStack: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
    },
    todayChip: {
        color: "primary.main",
        backgroundColor: "#e3f2fd"
    },
    shiftChip: {
        backgroundColor: "#e3f2fd",
        fontWeight: 500,
        color: "primary.main",
    },
    amendmentChip: {
        backgroundColor: orange[50],
        color: orange[900],
        fontWeight: 500,
        border: `0.063rem solid ${orange[200]}`,
    },
};

export default function ShiftAdminSummaryHeader({
    currentDate,
    activeView,
    monthRows,
    dayRows,
    showAddPerformerButton = false,
    onAddPerformerClick,
    isAddPerformerDisabled = false
}) {
    const {timezone} = useTimezone();
    const {t} = useTranslation();

    const currentDayShiftCount = useMemo(() => {
        return calculateTotalForRowField(dayRows, "totalShiftCount");
    }, [dayRows]);

    const currentDayAmendmentCount = useMemo(() => {
        return calculateTotalForRowField(dayRows, "amendmentCount");
    }, [dayRows]);

    const totalShiftCount = useMemo(() => {
        return calculateTotalForRowField(monthRows, "totalShiftCount");
    }, [monthRows]);

    const totalAmendmentCount = useMemo(() => {
        return calculateTotalForRowField(monthRows, "totalAmendmentCount");
    }, [monthRows]);

    const activeViewShiftCount = activeView === SHIFT_ADMIN_VIEWS.DAY
        ? currentDayShiftCount
        : totalShiftCount;

    const activeViewAmendmentCount = activeView === SHIFT_ADMIN_VIEWS.DAY
        ? currentDayAmendmentCount
        : totalAmendmentCount;

    const formattedDate = moment(currentDate)
        .tz(timezone)
        .format(activeView === SHIFT_ADMIN_VIEWS.MONTH ? MONTH_YEAR_FORMAT : FULL_DAY_DATE_FORMAT);

    const isCurrentDateToday = moment(currentDate)
        .tz(timezone)
        .isSame(moment().tz(timezone), "day");

    const isCurrentDateInCurrentMonth = moment(currentDate)
        .tz(timezone)
        .isSame(moment().tz(timezone), "month");

    const contextChipLabel = activeView === SHIFT_ADMIN_VIEWS.MONTH
        ? (isCurrentDateInCurrentMonth ? THIS_MONTH_CHIP_LABEL : null)
        : (isCurrentDateToday ? TODAY_CHIP_LABEL : null);

    return (
        <Box sx={styles.header}>
            <Stack sx={styles.headerContentStack}>
                <Stack sx={styles.dateContentStack}>
                    <Typography variant="h6">
                        {formattedDate}
                    </Typography>

                    {contextChipLabel && <Chip label={contextChipLabel} size="small" sx={styles.todayChip}/>}
                </Stack>

                <Stack sx={styles.summaryContentStack}>
                    <Chip label={t("common.shiftCountLabel", {count: activeViewShiftCount ?? 0})} variant="outlined" sx={styles.shiftChip}/>
                    <Chip label={t("common.amendmentCountLabel", {count: activeViewAmendmentCount ?? 0})} sx={styles.amendmentChip}/>

                    {
                        showAddPerformerButton
                        &&
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={onAddPerformerClick}
                            disabled={isAddPerformerDisabled}>
                            + Add Performer
                        </Button>
                    }
                </Stack>
            </Stack>
        </Box>);
}